import React, { useState, useEffect } from 'react';
import { Wallet, DollarSign, Users, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const formatBRL = (value: number) => `R$ ${(value ?? 0).toFixed(2)}`;

export const WithdrawalBalance: React.FC = () => {
    const [totalPending, setTotalPending] = useState<number>(0);
    const [sellers, setSellers] = useState<{ user_id: string; full_name: string | null; email: string | null; balance: number }[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await api.get('/banana/withdrawal-balance');
            setTotalPending(res.data.total_pending_withdrawal ?? 0);
            setSellers(res.data.sellers ?? []);
        } catch (e) {
            console.error('Erro ao carregar saldo para saque:', e);
            setError('Não foi possível carregar os dados.');
            setTotalPending(0);
            setSellers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Wallet className="text-orange-500" /> Saldo para Saque
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Soma do saldo disponível de todos os sellers. Mantenha este valor na adquirente Slimpay para honrar os saques.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white rounded-xl px-4 py-2 text-xs font-bold transition-all"
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Atualizar
                </button>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                    {error}
                </div>
            )}

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 max-w-2xl">
                <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="text-orange-400" size={28} />
                    <span className="text-gray-400 text-sm uppercase tracking-wider">Total a manter na Slimpay</span>
                </div>
                {loading ? (
                    <span className="text-3xl font-bold text-white">Carregando...</span>
                ) : (
                    <span className="text-3xl font-bold text-green-400">{formatBRL(totalPending)}</span>
                )}
            </div>

            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Users size={20} className="text-orange-500" /> Detalhamento por seller
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full text-left">
                    <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5">
                        <tr>
                            <th className="p-5 font-normal uppercase tracking-wider">Seller</th>
                            <th className="p-5 font-normal uppercase tracking-wider text-right">Saldo disponível</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? (
                            <tr>
                                <td colSpan={2} className="p-8 text-center text-gray-500">Carregando...</td>
                            </tr>
                        ) : sellers.length === 0 ? (
                            <tr>
                                <td colSpan={2} className="p-8 text-center text-gray-500">
                                    Nenhum seller com saldo disponível para saque.
                                </td>
                            </tr>
                        ) : (
                            sellers.map(s => (
                                <tr key={s.user_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                                    <td className="p-5">
                                        <div className="flex flex-col">
                                            <span className="text-white font-medium">{s.full_name || '—'}</span>
                                            <small className="text-gray-500 text-xs font-mono">{s.email || s.user_id}</small>
                                        </div>
                                    </td>
                                    <td className="p-5 text-right text-green-400 font-bold">
                                        {formatBRL(s.balance)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
